import React, { useState } from 'react';
import { DocumentTextIcon, ClockIcon, StarIcon } from '@heroicons/react/24/outline';

const SORT_OPTIONS = [
  { id: 'alphabet', label: 'Alphabetical' },
  { id: 'recent', label: 'Recent' },
  { id: 'popular', label: 'Most Popular' }
];

function DocumentGrid({ documents, category, searchQuery, searchResults, isSearching, onSelectDocument, topics = {}, topicsLoading = false }) {
  const [sortBy, setSortBy] = useState('alphabet');

  // Filter by backend search results (full-text search in PDF content)
  let filteredDocs = documents;
  if (searchQuery && searchQuery.trim() && searchResults !== null) {
    // Filter to only documents returned by backend search
    filteredDocs = documents.filter(doc => searchResults.includes(doc.name));
  }

  // Then filter by category
  if (category !== 'all') {
    filteredDocs = filteredDocs.filter(doc => doc.category === category);
  }

  // Sort documents
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    if (sortBy === 'alphabet') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'recent') {
      return (b.lastModified || 0) - (a.lastModified || 0);
    } else {
      return (b.views || 0) - (a.views || 0);
    }
  });

  return (
    <div className="h-full bg-galaxy-950 flex flex-col">
      {/* Header with sorting */}
      <div className="p-6 border-b border-galaxy-800/50">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-white">
            {category === 'all' ? 'All Documents' : documents.find(d => d.category === category)?.categoryName || 'Documents'}
            {isSearching && <span className="ml-3 text-sm text-galaxy-400">Searching...</span>}
          </h2>
          <div className="flex gap-2">
            {SORT_OPTIONS.map(option => (
              <button
                key={option.id}
                onClick={() => setSortBy(option.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  sortBy === option.id
                    ? 'bg-galaxy-600 text-white'
                    : 'bg-galaxy-800/40 text-galaxy-400 hover:bg-galaxy-700/40 hover:text-galaxy-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-galaxy-400">
          {sortedDocs.length} document{sortedDocs.length !== 1 ? 's' : ''}
          {searchQuery && searchQuery.trim() && !isSearching && (
            <span className="ml-2 text-galaxy-500">matching "{searchQuery}"</span>
          )}
        </p>
      </div>

      {/* Document Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
          {sortedDocs.map(doc => (
            <DocumentCard
              key={doc.name}
              document={doc}
              onSelectSection={(page) => onSelectDocument(doc, page)}
              sections={topics[doc.filename] || []}
              topicsLoading={topicsLoading}
            />
          ))}
        </div>

        {sortedDocs.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <DocumentTextIcon className="w-16 h-16 text-galaxy-700 mb-4" />
            <h3 className="text-lg font-medium text-galaxy-400 mb-2">No documents found</h3>
            <p className="text-sm text-galaxy-500">Try selecting a different category</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentCard({ document, onSelectSection, sections = [], topicsLoading = false }) {
  const getCategoryColor = (category) => {
    switch (category) {
      case 'standards': return 'from-blue-500 to-blue-700';
      case 'oem_manuals': return 'from-green-500 to-green-700';
      case 'installation_guides': return 'from-purple-500 to-purple-700';
      default: return 'from-galaxy-500 to-galaxy-700';
    }
  };

  return (
    <div
      onClick={() => onSelectSection(1)}
      className="bg-galaxy-900/60 backdrop-blur-sm border border-galaxy-800/50 rounded-xl p-5 cursor-pointer transition-all hover:transform hover:-translate-y-1 hover:shadow-xl hover:shadow-galaxy-500/20 hover:border-galaxy-600/50"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getCategoryColor(document.category)} flex items-center justify-center`}>
          <DocumentTextIcon className="w-6 h-6 text-white" />
        </div>
        <span className="px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded text-xs font-semibold">
          Active
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-semibold text-white mb-2 line-clamp-2">
        {document.title}
      </h3>

      {/* Metadata */}
      <div className="flex items-center gap-3 text-xs text-galaxy-400 mb-3">
        <div className="flex items-center gap-1">
          <DocumentTextIcon className="w-3.5 h-3.5" />
          <span>{document.pages || '~'} pages</span>
        </div>
        {document.lastModified && (
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            <span>Updated {new Date(document.lastModified).toLocaleDateString()}</span>
          </div>
        )}
        {document.views && (
          <div className="flex items-center gap-1">
            <StarIcon className="w-3.5 h-3.5" />
            <span>{document.views} views</span>
          </div>
        )}
      </div>

      {/* Sections */}
      {topicsLoading ? (
        <div className="mt-4 pt-3 border-t border-galaxy-800/50">
          <div className="text-xs text-galaxy-500 mb-2">Loading topics...</div>
        </div>
      ) : sections && sections.length > 0 ? (
        <div className="mt-4 pt-3 border-t border-galaxy-800/50">
          <div className="text-xs text-galaxy-500 mb-2">Key Sections:</div>
          <div className="flex flex-wrap gap-2">
            {sections.slice(0, 5).map((section, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectSection(section.page || 1);
                }}
                className="px-2.5 py-1 bg-galaxy-800/50 text-galaxy-300 hover:bg-galaxy-700/50 hover:text-white border border-galaxy-700/30 hover:border-galaxy-600/50 rounded-full text-xs transition-colors"
              >
                {section.title}
                {section.page && <span className="ml-1 text-galaxy-500">p.{section.page}</span>}
              </button>
            ))}
            {sections.length > 5 && (
              <span className="px-2.5 py-1 text-galaxy-500 text-xs">
                +{sections.length - 5} more
              </span>
            )}
          </div>
        </div>
      ) : null}

      {/* Tags */}
      {document.tags && document.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {document.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-galaxy-700/30 text-galaxy-400 border border-galaxy-600/30 rounded text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default DocumentGrid;
